import { useEffect, useRef, useState } from "react";
import { priceLabel } from "./catalog";

function Card({ p, w, h, node, catalog, fs, titleH }) {
  const [broken, setBroken] = useState(false);
  const s = node.styles || {};
  const price = node.carouselShowPrice ? priceLabel(p, catalog, node) : "";
  return <div style={{ width: w, flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center" }}>
    <div style={{ width: w, height: h, borderRadius: node.carouselItemRadius ?? 4, overflow: "hidden", boxShadow: node.carouselItemShadow === false ? "none" : "0 10px 24px rgba(0,0,0,0.35)", background: "#cbd5e1" }}>
      {p.image && !broken
        ? <img src={p.image} alt={p.title} draggable={false} onError={() => setBroken(true)} style={{ width: "100%", height: "100%", objectFit: node.carouselObjectFit || "cover", display: "block", pointerEvents: "none" }} />
        : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, fontWeight: 700, color: "#fff" }}>{(p.title || "?").charAt(0)}</div>}
    </div>
    {titleH > 0 && <div style={{ height: titleH, width: "100%", paddingTop: 8, boxSizing: "border-box", textAlign: "center", color: s.color || "#ffffff", fontSize: fs, fontFamily: s.fontFamily || "Inter", fontWeight: s.fontWeight || "600", overflow: "hidden" }}>
      {node.carouselShowTitle && <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.title}</div>}
      {price && <div style={{ fontSize: fs * 0.85, opacity: 0.85 }}>{price}</div>}
    </div>}
  </div>;
}

export function CarouselView({ node, products, catalog, onSelect, interactive = true }) {
  const s = node.styles || {};
  const W = s.width || 600;
  const H = s.height || 260;
  const mode = node.carouselMode || "marquee";
  const fs = s.fontSize || 14;
  const gap = node.carouselGap ?? 32;
  const showDots = mode !== "marquee" && node.carouselShowDots !== false;
  const titleH = node.carouselShowTitle || node.carouselShowPrice ? Math.round(fs * (node.carouselShowTitle && node.carouselShowPrice ? 3 : 1.9)) : 0;
  const vPad = 16;
  const imgH = Math.max(20, H - titleH - vPad * 2 - (showDots ? 22 : 0));
  const ratio = node.carouselImageRatio || 1.45;
  const itemW = mode === "slides" ? W : Math.max(20, node.carouselItemWidth || Math.round(imgH / ratio));
  const cardH = mode === "slides" ? imgH : (node.carouselItemWidth ? Math.round(itemW * ratio) : imgH);
  const n = products.length;
  const stride = itemW + gap;

  const rootRef = useRef(null);
  const trackRef = useRef(null);
  const offset = useRef(0);
  const vel = useRef(0);
  const drag = useRef(null);
  const pausedUntil = useRef(0);
  const [idx, setIdx] = useState(0);
  const [dragDx, setDragDx] = useState(0);
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  const setW = n * stride;
  const copies = mode === "marquee" && n ? Math.max(2, Math.ceil(W / Math.max(1, setW)) + 1) : 1;
  const speed = node.carouselSpeed ?? 40;
  const dir = node.carouselDirection === "right" ? -1 : 1;

  useEffect(() => {
    if (mode !== "marquee" || !n) return;
    let raf;
    let last = performance.now();
    const step = now => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      if (!drag.current) {
        if (Math.abs(vel.current) > 4) {
          offset.current += vel.current * dt;
          vel.current *= Math.pow(0.04, dt);
        } else if (interactive && now > pausedUntil.current) {
          offset.current += speed * dt * dir;
        }
      }
      offset.current = ((offset.current % setW) + setW) % setW;
      if (trackRef.current) trackRef.current.style.transform = `translate3d(${-offset.current}px,0,0)`;
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [mode, n, setW, speed, dir, interactive]);

  useEffect(() => {
    if (mode === "marquee" || !n || !interactive) return;
    const ms = Math.max(1, node.carouselInterval ?? 4) * 1000;
    const t = setInterval(() => {
      if (drag.current || performance.now() < pausedUntil.current) return;
      setIdx(i => (i + 1) % n);
    }, ms);
    return () => clearInterval(t);
  }, [mode, n, interactive, node.carouselInterval]);

  useEffect(() => { if (idx >= n) setIdx(0); }, [n, idx]);

  const resumeLater = () => { pausedUntil.current = performance.now() + (node.carouselResumeDelay ?? 2.5) * 1000; };

  const itemAt = target => {
    const el = target && target.closest ? target.closest("[data-ci]") : null;
    return el ? Number(el.getAttribute("data-ci")) : -1;
  };

  const onDown = e => {
    if (!interactive || !n) return;
    const rect = rootRef.current.getBoundingClientRect();
    drag.current = { x: e.clientX, t: performance.now(), lastX: e.clientX, lastT: performance.now(), start: offset.current, scale: rect.width / W || 1, moved: false, item: itemAt(e.target) };
    vel.current = 0;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
  };

  const onMove = e => {
    const d = drag.current;
    if (!d) return;
    const dx = (e.clientX - d.x) / d.scale;
    if (Math.abs(dx) > 8) d.moved = true;
    if (!d.moved) return;
    const now = performance.now();
    const v = ((e.clientX - d.lastX) / d.scale) / Math.max(1, now - d.lastT) * 1000;
    d.v = v;
    d.lastX = e.clientX;
    d.lastT = now;
    if (mode === "marquee") offset.current = d.start - dx;
    else setDragDx(dx);
  };

  const onUp = e => {
    const d = drag.current;
    if (!d) return;
    drag.current = null;
    resumeLater();
    const dx = (e.clientX - d.x) / d.scale;
    if (!d.moved) {
      if (d.item < 0) return;
      const real = d.item % n;
      if (mode !== "marquee" && real !== idx && node.carouselTapToCenter !== false) { setIdx(real); return; }
      onSelectRef.current?.(products[real]);
      return;
    }
    if (mode === "marquee") { vel.current = -(d.v || 0); return; }
    setDragDx(0);
    const steps = Math.abs(dx) > stride * 0.2 || Math.abs(d.v || 0) > 500 ? Math.max(1, Math.round(Math.abs(dx) / stride)) : 0;
    if (steps) setIdx(i => ((i + (dx < 0 ? steps : -steps)) % n + n) % n);
  };

  const rootStyle = { width: "100%", height: "100%", position: "relative", overflow: "hidden", touchAction: "pan-y", userSelect: "none", WebkitUserSelect: "none", pointerEvents: interactive ? "auto" : "none" };

  if (!n) {
    return <div style={{ ...rootStyle, display: "flex", alignItems: "center", justifyContent: "center", color: s.color || "#ffffff", opacity: 0.6, fontSize: fs, fontFamily: s.fontFamily || "Inter" }}>{node.carouselEmptyText || "No items"}</div>;
  }

  if (mode === "marquee") {
    const all = [];
    for (let c = 0; c < copies; c++) products.forEach((p, i) => all.push({ p, k: `${c}-${p.id}-${i}`, ci: c * n + i }));
    return <div ref={rootRef} style={rootStyle} onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}>
      <div ref={trackRef} style={{ position: "absolute", left: 0, top: vPad, display: "flex", gap, paddingRight: gap, willChange: "transform", transform: `translate3d(${-offset.current}px,0,0)` }}>
        {all.map(({ p, k, ci }) => (
          <div key={k} data-ci={ci} style={{ cursor: interactive ? "pointer" : "default", height: cardH + titleH, display: "flex", alignItems: "flex-end" }}>
            <Card p={p} w={itemW} h={cardH} node={node} catalog={catalog} fs={fs} titleH={titleH} />
          </div>
        ))}
      </div>
    </div>;
  }

  const coverflow = mode === "coverflow";
  const liveFrac = dragDx / stride;
  const baseX = (W - itemW) / 2 - idx * stride + dragDx;
  return <div ref={rootRef} style={{ ...rootStyle, perspective: coverflow ? 1200 : undefined }} onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}>
    <div style={{ position: "absolute", left: 0, top: vPad, height: cardH + titleH, transform: `translate3d(${baseX}px,0,0)`, transition: dragDx ? "none" : "transform 0.55s cubic-bezier(.22,.8,.3,1)", transformStyle: "preserve-3d" }}>
      {products.map((p, i) => {
        const d = i - idx + liveFrac;
        const ad = Math.min(Math.abs(d), 3);
        const fx = coverflow
          ? { transform: `rotateY(${Math.max(-50, Math.min(50, -d * 40))}deg) scale(${1 - Math.min(ad, 2) * 0.14})`, zIndex: 100 - Math.round(ad * 10), opacity: ad > 2.6 ? 0 : 1 }
          : {};
        return <div key={p.id + i} data-ci={i} style={{ position: "absolute", left: i * stride, top: 0, cursor: interactive ? "pointer" : "default", transition: dragDx ? "none" : "transform 0.55s cubic-bezier(.22,.8,.3,1), opacity 0.4s", ...fx }}>
          <Card p={p} w={itemW} h={cardH} node={node} catalog={catalog} fs={fs} titleH={titleH} />
        </div>;
      })}
    </div>
    {showDots && <div style={{ position: "absolute", left: 0, right: 0, bottom: 6, display: "flex", justifyContent: "center", gap: 7 }}>
      {products.map((p, i) => (
        <span key={p.id + i} onPointerDown={e => e.stopPropagation()} onClick={() => { if (interactive) { setIdx(i); resumeLater(); } }} style={{ width: i === idx ? 18 : 7, height: 7, borderRadius: 4, background: node.carouselDotColor || s.color || "#ffffff", opacity: i === idx ? 0.95 : 0.4, transition: "all 0.3s", cursor: "pointer" }} />
      ))}
    </div>}
  </div>;
}
